import getCurrentSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import getHeaderAriaLabel from '../utils/getHeaderAriaLabel';
import getSelectedFolderId from 'owa-mail-store/lib/utils/getSelectedFolderId';
import isUnifiedSearchEnabled from 'mini-search-common/lib/utils/isUnifiedSearchEnabled';
import mailStore from 'owa-mail-store/lib/store/Store';
import Module from 'mini-module-switcher/lib/schema/Module';
import React, { useRef } from 'react';
import setShouldShowSearchBox from 'mini-mail-list-store/lib/actions/setShouldShowSearchBox';
import strings from 'owa-strings/owa-mini-strings';
import switchModule from 'owa-mail-store/lib/actions/switchModule';
import { getActiveMailListFilterPropsForSelectedView } from 'mini-mail-list-filter/lib/utils/getActiveMailListFilterPropsForSelectedView';
import { getFolderName } from 'mini-mail-list-store/lib/selectors/getFolderName';
import { getModalViewState, toggleModalViewState } from 'mini-prevent-compose';
import { getOpenInNewWindowIcon } from 'window-utils/lib/getOpenInNewWindowIcon';
import { Header, HeaderIcon } from 'mini-header';
import { LAUNCH_MAIL_SEARCH } from 'mini-mail-workflow-tracking/lib/constants/SearchWorkflowConstants';
import { LAUNCH_SEARCH } from 'mini-search-common/lib/utils/searchWorkFlowConstants';
import { LazyComposePreventionModal } from 'mini-prevent-compose';
import { MiniIcons } from 'owa-mini-icons';
import { ModalDialogType } from 'window-utils/lib/getOpenInNewWindowIcon';
import { observer } from 'mobx-react-lite';
import { onMailListFilterClickCallback } from 'mini-mail-list-filter/lib/utils/onMailListFilterClickCallback';
import { onNewCompose } from '../utils/onNewCompose';
import { onOpenFolderPane } from '../utils/onOpenFolderPane';
import { PerformanceDatapoint } from 'owa-analytics';
import { shouldShowMailListHeaderForFolderId } from 'mini-mail-list-view/lib/utils/mailList/shouldShowMailListHeaderForFolderId';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { TitleJustification } from 'mini-header/lib/components/Header.types';
const styles = require('./MailSurfaceHeader.scss');

interface MailSurfaceForwardedRefs {
    navigationButtonRef: React.RefObject<any>;
    searchButtonRef: React.RefObject<any>;
    composeButtonRef: React.RefObject<any>;
}

interface MailSurfaceHeaderProps {
    onSearchStartCallback?: (searchPerfDataPoint: PerformanceDatapoint) => void;
    forwardedRefs?: MailSurfaceForwardedRefs;
}

const MailSurfaceHeader = (props: MailSurfaceHeaderProps) => {
    const {
        onSearchStartCallback,
        forwardedRefs: {
            navigationButtonRef,
            searchButtonRef,
            composeButtonRef,
        } = {} as MailSurfaceForwardedRefs,
    } = props;

    // create the refs
    const mailFilterRef = useRef<HTMLDivElement>();

    const { folderTable } = mailStore.folders;
    const selectedFolderId = getSelectedFolderId();
    let folderName: string;

    if (selectedFolderId && folderTable.size > 0) {
        const selectedFolder = folderTable.get(selectedFolderId);

        // Fix for Bug: 34288
        // selectedFolder comes as null some times when pressing hardware back button multiple times.
        // Right now, doing a null check to avoid the error but created a WI 34429 on this.
        folderName = selectedFolder ? selectedFolder.DisplayName : '';
    }

    // The left icon
    const leftIcon: HeaderIcon = {
        icon: MiniIcons.GlobalNavButton,
        onClick: onOpenFolderPane,
        onActivate: onOpenFolderPane,
        ariaHasPopup: true,
        iconRef: navigationButtonRef,
        ariaLabel: strings.folderNavigationButtonAriaLabel,
        isPrimary: true,
    };

    // The right icons
    const onSearchStart = () => {
        onSearchStartCallback &&
            onSearchStartCallback(new PerformanceDatapoint('SearchBoxAppearance'));

        if (isUnifiedSearchEnabled()) {
            signalWorkflowEvent(LAUNCH_SEARCH, { source: getFolderName() });
            switchModule(Module.Search);
        } else {
            signalWorkflowEvent(LAUNCH_MAIL_SEARCH, { source: getFolderName() });
            setShouldShowSearchBox(true /* shouldShowSearchBox */);
        }
    };

    const {
        activeViewFilter,
        icon,
        label,
        ariaLabel,
        isPressed,
    } = getActiveMailListFilterPropsForSelectedView(getCurrentSelectedTableView());

    const composeIconClick = () => {
        const isReadOnly = mailStore.isReadOnly;
        if (isReadOnly && !getModalViewState().isShown) {
            toggleModalViewState();
        } else {
            onNewCompose();
        }
    };

    const mailFilterButtons: HeaderIcon[] = !shouldShowMailListHeaderForFolderId(selectedFolderId)
        ? [
              {
                  icon: icon,
                  onClick: onMailListFilterClickCallback(activeViewFilter, mailFilterRef),
                  iconRef: mailFilterRef,
                  ariaLabel: ariaLabel || label,
                  isPrimary: true,
                  iconStyle: isPressed ? styles.isButtonPressed : null,
              },
          ]
        : [];

    const rightIcons: HeaderIcon[] = [
        getOpenInNewWindowIcon(),
        ...mailFilterButtons,
        {
            icon: MiniIcons.Search,
            onClick: onSearchStart,
            iconRef: searchButtonRef,
            ariaLabel: strings.searchButtonAriaLabel,
            isPrimary: true,
        },
        {
            icon: MiniIcons.Add,
            onClick: composeIconClick,
            iconRef: composeButtonRef,
            ariaLabel: strings.composeNewMailAriaLabel,
            isPrimary: true,
            isBordered: true,
        },
    ];

    return (
        <div>
            {mailStore.isReadOnly && getModalViewState().isShown && (
                <LazyComposePreventionModal type={ModalDialogType.MailModule} />
            )}
            <Header
                leftIcon={leftIcon}
                rightIcon={rightIcons}
                title={folderName}
                titleAriaLabel={getHeaderAriaLabel(folderName)}
                titleJustification={TitleJustification.LEFT}
                titleStyles={styles.primaryTitle}
                headerStyles={styles.mailSurfaceHeader}
                showTitle
                extensionHeaderStyles={{
                    containerStyle: styles.extensionHeader,
                }}
            />
        </div>
    );
};

export default observer(MailSurfaceHeader);
